import type { StyleSpecification } from 'maplibre-gl';

const AZURE_TILE_API_VERSION = '2024-04-01';

/** Probe timeout. Long enough for a cold TLS handshake, short enough not to
 *  eat into the export's 15s map-ready budget. */
const PROBE_TIMEOUT_MS = 4_000;

export enum AzureSourceId {
  Satellite = 'azure-satellite',
  Labels = 'azure-labels',
}

export enum AzureTilesetId {
  Imagery = 'microsoft.imagery',
  LabelsRoad = 'microsoft.base.labels.road',
}

function azureTileUrl(tilesetId: AzureTilesetId, subscriptionKey: string): string {
  return `https://atlas.microsoft.com/map/tile?api-version=${AZURE_TILE_API_VERSION}&tilesetId=${tilesetId}&zoom={z}&x={x}&y={y}&subscription-key=${subscriptionKey}`;
}

/**
 * Whether Azure will actually serve imagery tiles, checked by fetching the
 * cheapest one. A valid key is not enough: the account's CORS allowlist accepts
 * exact origins only, so any host that isn't listed gets 403 on every tile (as
 * do entitlement problems, like a tier without `microsoft.imagery`). MapLibre
 * reports that as an `error` event long after the map has committed to the
 * style, leaving a white basemap that still reaches `idle`, so callers probe
 * first and pick streets before constructing the map.
 *
 * The allowlist is enforced against browsers only, which is why this can't move
 * server-side: a check from the server would pass while the iframe still failed.
 *
 * Only a positive answer is cached (per JS realm, so per embed iframe). Pinning
 * a negative one would let a single timeout or blip downgrade every later map
 * in the realm, turning an author's explicit satellite choice into streets for
 * a reason that has since gone away.
 */
let satelliteProbe: Promise<boolean> | null = null;

export function isAzureSatelliteUsable(subscriptionKey: string): Promise<boolean> {
  satelliteProbe ??= probeAzureSatellite(subscriptionKey).then(usable => {
    if (!usable) satelliteProbe = null;
    return usable;
  });
  return satelliteProbe;
}

async function probeAzureSatellite(subscriptionKey: string): Promise<boolean> {
  // Zoom 1 is the imagery tileset's minzoom, so this is its smallest tile.
  const url = azureTileUrl(AzureTilesetId.Imagery, subscriptionKey)
    .replace('{z}', '1')
    .replace('{x}', '0')
    .replace('{y}', '0');

  try {
    const response = await fetch(url, { signal: AbortSignal.timeout(PROBE_TIMEOUT_MS) });
    return response.ok;
  } catch {
    // A rejected fetch is itself the answer: Azure omits CORS headers on its
    // error responses, so a 403 surfaces here as a network error.
    return false;
  }
}

/** Test seam: the probe result is cached for the page's lifetime. */
export function resetAzureSatelliteProbe(): void {
  satelliteProbe = null;
}

export function getAzureSatelliteStyle(subscriptionKey: string): StyleSpecification {
  return {
    version: 8,
    name: 'Azure Maps Satellite with Labels',
    sources: {
      [AzureSourceId.Satellite]: {
        type: 'raster',
        tiles: [azureTileUrl(AzureTilesetId.Imagery, subscriptionKey)],
        tileSize: 256,
        minzoom: 1,
        maxzoom: 19,
      },
      [AzureSourceId.Labels]: {
        type: 'raster',
        tiles: [azureTileUrl(AzureTilesetId.LabelsRoad, subscriptionKey)],
        tileSize: 256,
        minzoom: 0,
        maxzoom: 22,
      },
    },
    layers: [
      {
        id: 'satellite',
        type: 'raster',
        source: AzureSourceId.Satellite,
        minzoom: 1,
        maxzoom: 23,
      },
      {
        id: 'labels',
        type: 'raster',
        source: AzureSourceId.Labels,
        minzoom: 0,
        maxzoom: 23,
      },
    ],
    glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
  };
}
